import { Injectable, inject } from '@angular/core';

import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, Router } from '@angular/router';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AuthService, ConfigService } from '../services';
import { ApiService, CalendarServerToken, emailLogin, getUserInfo } from '@cue/api';

@Injectable({ providedIn: 'root' })
export class OAuthCallbackGuard implements CanActivate {
  private router = inject(Router);
  private authService = inject(AuthService);
  private configService = inject(ConfigService);
  private apiService = inject(ApiService);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const authCode = route.queryParams['code'];
    if (this.configService.getMode() === 'touchone-calendar') {
      const redirectUrl = localStorage.getItem('oauthRedirectUrl');
      const scope = localStorage.getItem('scope');
      const connectionAgentId = parseInt(localStorage.getItem('connectionAgentId'), 10);
      const profilePairing = this.authService.isProfileExternalLoginPairing();
      return this.authService.getCalendarTokenFromServer(authCode, redirectUrl, scope, connectionAgentId).pipe(
        switchMap((r) => {
          if ((r as any).success === false) {
            if (profilePairing) {
              return of(this.finishProfilePairing());
            }
            return of(this.failLogin());
          }

          if (profilePairing) {
            return this.pairWithCurrentUser(r);
          }

          if (r.paired) {
            this.authService.saveApiToken(r.token);
            this.authService.saveApiRefreshToken(r.refreshToken);
            const returnUrl = sessionStorage.getItem('redirect_url');
            sessionStorage.removeItem('redirect_url');
            return of(this.router.parseUrl(returnUrl ?? '/dashboard'));
          }

          this.authService.removeApiToken();
          this.authService.removeApiRefreshToken();
          return of(this.router.parseUrl('/account/pairing?pairinguid=' + r.pairingUID));
        })
      );
    } else {
      return this.authService.getTokenFromServer(authCode).pipe(
        tap((r) => {
          if (r?.success) {
            this.authService.saveApiToken(r.data!.token);
            this.authService.saveApiRefreshToken(r.data!.refreshToken);
          } else {
            this.authService.removeApiToken();
            this.authService.removeApiRefreshToken();
          }
        }),
        map((r) => {
          if (r.success) {
            const redirectUrl = sessionStorage.getItem('redirect_url')!;
            sessionStorage.removeItem('redirect_url');
            return this.router.parseUrl(redirectUrl !== undefined ? redirectUrl : '/dashboard');
          } else {
            return this.router.parseUrl('/account/login?error=login_unsuccessful');
          }
        })
      );
    }
  }

  private pairWithCurrentUser(response: CalendarServerToken) {
    if (response.paired || !response.pairingUID) {
      return of(this.finishProfilePairing());
    }

    return this.apiService.call(getUserInfo()).pipe(
      switchMap((currentUser) => {
        const email = currentUser.data?.email;
        if (!email) {
          return of(this.finishProfilePairing());
        }

        return this.apiService.call(emailLogin(email, response.pairingUID)).pipe(
          map((id) => {
            this.authService.clearProfileExternalLoginPairing();
            return this.router.createUrlTree(['/account/email-link'], {
              queryParams: {
                id,
                pairinguid: response.pairingUID,
                email,
              },
            });
          })
        );
      }),
      catchError(() => of(this.finishProfilePairing()))
    );
  }

  private finishProfilePairing() {
    this.authService.clearProfileExternalLoginPairing();
    sessionStorage.removeItem('redirect_url');
    return this.router.parseUrl('/profile?tab=linked');
  }

  private failLogin() {
    this.authService.removeApiToken();
    this.authService.removeApiRefreshToken();
    return this.router.parseUrl('/account/login?error=login_unsuccessful');
  }
}
